/**
 * Geometry of the shareable picture, with no canvas in sight so it can be
 * unit-tested. Strings arrive already localized; the layout neither knows
 * nor cares which language it is laying out.
 *
 * A list of names: the picture is for a Discord trade channel, where "which
 * plan is that" is exactly the question it has to answer. Two columns,
 * because the longest Russian names run to 65 characters and a truncated
 * name defeats the purpose; the picture is tall rather than wide, which is
 * how a phone shows it anyway.
 */

export interface CardItem {
  id: string;
  name: string;
  done: boolean;
}

export interface CardGroup {
  id: string;
  title: string;
  items: CardItem[];
}

export interface CardInput {
  title: string;
  subtitle: string;
  footer: string;
  /** Drawn instead of the list when a filter leaves nothing to show. */
  emptyText: string;
  groups: CardGroup[];
  /** Brag mode: collected items only, untouched groups dropped. */
  onlyDone: boolean;
}

export interface CardCell {
  x: number;
  y: number;
  item: CardItem;
}

export interface CardBlock {
  y: number;
  height: number;
  title: string;
  done: number;
  total: number;
  cells: CardCell[];
}

export interface CardLayout {
  width: number;
  height: number;
  columnWidth: number;
  title: string;
  subtitle: string;
  footer: string;
  done: number;
  total: number;
  blocks: CardBlock[];
  empty: string | null;
}

/** Logical pixels; the renderer scales for DPR. */
export const CARD = {
  width: 880,
  pad: 32,
  columns: 2,
  headerHeight: 116,
  groupTitleHeight: 28,
  rowHeight: 28,
  groupGap: 14,
  footerHeight: 44,
  emptyHeight: 48,
} as const;

export function layoutCard(input: CardInput): CardLayout {
  const columnWidth = (CARD.width - CARD.pad * 2) / CARD.columns;
  const blocks: CardBlock[] = [];
  let done = 0;
  let total = 0;
  let y = CARD.headerHeight;

  for (const group of input.groups) {
    const groupDone = group.items.filter((i) => i.done).length;
    done += groupDone;
    total += group.items.length;

    const items = input.onlyDone ? group.items.filter((i) => i.done) : group.items;
    if (items.length === 0) continue;

    const rows = Math.ceil(items.length / CARD.columns);
    const cells = items.map((item, i) => ({
      x: CARD.pad + (i % CARD.columns) * columnWidth,
      y: y + CARD.groupTitleHeight + Math.floor(i / CARD.columns) * CARD.rowHeight,
      item,
    }));
    const height = CARD.groupTitleHeight + rows * CARD.rowHeight;
    blocks.push({ y, height, title: group.title, done: groupDone, total: group.items.length, cells });
    y += height + CARD.groupGap;
  }

  const empty = blocks.length === 0;
  const bodyBottom = empty ? y + CARD.emptyHeight : y - CARD.groupGap;

  return {
    width: CARD.width,
    height: bodyBottom + CARD.footerHeight,
    columnWidth,
    title: input.title,
    subtitle: input.subtitle,
    footer: input.footer,
    done,
    total,
    blocks,
    empty: empty ? input.emptyText : null,
  };
}
